"use client";

import { KloktunUI, Theme } from "@kloktun/uikit";
import type { IconProp } from "@kloktun/uikit/lib/components/icon";
import type { ReactNode } from "react";
import { createContext, useContext, useEffect, useState } from "react";

export interface ThemeItem {
  icon: IconProp;
  name: string;
  theme: Theme;
}

interface ThemeContextProps {
  theme: Theme;
  setTheme: (themeName: string) => void;

  themes: ThemeItem[];
}

const ThemeContext = createContext<ThemeContextProps>({
  theme: new Theme({}),
  themes: [],
  setTheme: (themeName) => {},
});

export const useThemeContext = () => useContext(ThemeContext);

export const useTheme = () => {
  const { theme, setTheme } = useThemeContext();
  return { theme, setTheme };
};

export const useThemes = () => {
  const { themes } = useThemeContext();
  return themes;
};

interface ThemeProviderProps {
  initialThemeName: "dark" | "light";
  themes: ThemeItem[];

  children: ReactNode;
}

export const ThemeProvider = ({
  children,
  initialThemeName,
  themes,
}: ThemeProviderProps) => {
  const [theme, setThemeState] = useState(
    themes.find((t) => t.name === initialThemeName)?.theme || themes[0].theme
  );

  const setTheme = (themeName: string) => {
    const selectedTheme = themes.find((t) => t.name === themeName);
    if (selectedTheme) {
      setThemeState(selectedTheme.theme);
      localStorage.setItem("kloktun_theme", themeName);
      window.document
        .querySelector('meta[name="theme-color"]')
        ?.setAttribute(
          "content",
          selectedTheme.theme.backgroundBody ?? "#ffffff"
        );
    }
  };

  useEffect(() => {
    const savedThemeName = localStorage.getItem("kloktun_theme");

    if (savedThemeName) {
      const savedTheme = themes.find((t) => t.name === savedThemeName);
      if (savedTheme) setThemeState(savedTheme.theme);
    }
  }, [themes]);

  return (
    <ThemeContext.Provider
      value={{
        theme,
        setTheme,
        themes,
      }}
    >
      <KloktunUI theme={theme}>{children}</KloktunUI>
    </ThemeContext.Provider>
  );
};
